import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Eye, AlertTriangle, Users, Activity, Clock, Search, RefreshCw,
  ChevronDown, Lock, FileText, CheckCircle, Filter, Download, Loader2, MapPin
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  "user_logged_in": "User Login",
  "auth.login_success": "Login Success",
  "auth.login_failed": "Login Failed",
  "auth.password_reset_requested": "Password Reset Requested",
  "auth.password_reset_completed": "Password Reset Completed",
  "auth.token_refreshed": "Token Refreshed",
  "deal.stage_update": "Deal Stage Changed",
  "deal.stage_updated": "Deal Stage Changed",
  "admin.deal_correction": "Admin Deal Correction",
  "admin.approve_lender_selection": "Lender Selection Approved",
  "admin.reject_lender_selection": "Lender Selection Rejected",
  "lender.deal_viewed": "Lender Viewed Deal",
  "lender.accept_platform_terms": "Lender Accepted Terms",
  "borrower.term_sheet_select": "Borrower Selected Term Sheet",
  "lender.term_sheet_submit": "Term Sheet Submitted",
  "document.uploaded": "Document Uploaded",
  "document.approved": "Document Approved",
  "document.rejected": "Document Rejected",
  "user.updated": "User Updated",
  "user_registered": "User Registered",
  "stage_updated": "Stage Updated",
};

const ROLE_COLORS = {
  admin: "bg-purple-100 text-purple-700 border-purple-200",
  lender: "bg-blue-100 text-blue-700 border-blue-200",
  borrower: "bg-emerald-100 text-emerald-700 border-emerald-200",
  investor: "bg-amber-100 text-amber-700 border-amber-200",
  system: "bg-slate-100 text-slate-600 border-slate-200",
  unknown: "bg-gray-100 text-gray-500 border-gray-200",
};

export default function SecurityDashboard() {
  const [data, setData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [viewFilter, setViewFilter] = useState("7d"); // for lender views

  // Audit explorer filters
  const [logAction, setLogAction] = useState("");
  const [logEntity, setLogEntity] = useState("");
  const [logRole, setLogRole] = useState("");
  const [logLimit, setLogLimit] = useState(50);

  const fetchDashboard = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [dashRes, logRes] = await Promise.all([
        axios.get(`${API}/admin/security/dashboard`),
        axios.get(`${API}/admin/security/audit-logs`, { params: {
          action: logAction || undefined,
          entity_type: logEntity || undefined,
          limit: logLimit,
        }}),
      ]);
      setData(dashRes.data);
      setAuditLogs(logRes.data.logs || []);
      setLogTotal(logRes.data.total || 0);
    } catch (err) {
      toast.error("Failed to load security dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [logAction, logEntity, logLimit]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchDashboard(true), 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-6 w-6 animate-spin text-[#b89d5a]" />
    </div>
  );

  const s = data?.summary || {};
  const lenderViews = data?.lender_deal_views || [];
  const highRisk = data?.high_risk_actions || [];
  const recentEntries = data?.recent_entries || [];
  const topActors = data?.top_actors || [];
  const loginEvents = data?.login_events || [];
  const suspiciousAlerts = data?.suspicious_alerts || [];

  const fmt = (n) => n?.toLocaleString?.() || "0";
  const fmtTime = (ts) => {
    if (!ts) return "-";
    const d = new Date(ts);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };
  const fmtDate = (ts) => {
    if (!ts) return "-";
    return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const labelAction = (a) => ACTION_LABELS[a] || a?.replace(/[._]/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "Unknown";

  return (
    <div className="space-y-6" data-testid="security-dashboard">

      {/* ═══ Header ═══ */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1B2B1A] flex items-center justify-center">
            <Shield className="h-5 w-5 text-[#e5c98b]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0A0A0A] tracking-tight">Security & Compliance</h2>
            <p className="text-xs text-[#888]">Real-time audit monitoring and confidentiality tracking</p>
          </div>
        </div>
        <button onClick={() => fetchDashboard(true)} disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e8e0d0] text-xs text-[#666] hover:bg-[#faf7f2] transition-colors disabled:opacity-50"
          data-testid="refresh-security-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* ═══ Summary Cards ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="security-summary-cards">
        <SummaryCard label="Total Audit Entries" value={fmt(s.total_audit_entries)} sub={`${fmt(s.entries_7d)} this week`} icon={FileText} color="text-[#1B2B1A]" bg="bg-[#1B2B1A]/5" />
        <SummaryCard label="Active Users (7d)" value={fmt(s.unique_users_7d)} sub={`${fmt(s.entries_24h)} events today`} icon={Users} color="text-blue-700" bg="bg-blue-50" />
        <SummaryCard label="High Risk Actions" value={fmt(s.high_risk_actions_24h)} sub={s.high_risk_actions_24h === 0 ? "All clear" : "Last 24 hours"} icon={AlertTriangle} color={s.high_risk_actions_24h === 0 ? "text-emerald-700" : "text-red-600"} bg={s.high_risk_actions_24h === 0 ? "bg-emerald-50" : "bg-red-50"} />
        <SummaryCard label="Lender Deal Views (7d)" value={fmt(s.lender_views_7d)} sub="Confidentiality events" icon={Eye} color="text-amber-700" bg="bg-amber-50" />
      </div>

      {/* ═══ Suspicious Activity Alerts ═══ */}
      {suspiciousAlerts.length > 0 && (
        <div className="rounded-xl border-2 border-red-300 bg-red-50 overflow-hidden" data-testid="suspicious-alerts-panel">
          <div className="px-5 py-3 border-b border-red-200 bg-red-100/50 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <h3 className="text-sm font-bold text-red-800">Suspicious Activity Detected</h3>
            <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold">{suspiciousAlerts.length}</span>
          </div>
          <div className="divide-y divide-red-200">
            {suspiciousAlerts.map((alert, i) => (
              <div key={i} className="px-5 py-3 flex items-start gap-3" data-testid={`alert-${i}`}>
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${alert.severity === "high" ? "bg-red-500" : "bg-amber-400"}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-red-800">{alert.user_name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${ROLE_COLORS[alert.role] || ROLE_COLORS.unknown}`}>{alert.role}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${alert.severity === "high" ? "bg-red-200 text-red-700" : "bg-amber-200 text-amber-700"}`}>{alert.severity}</span>
                  </div>
                  <p className="text-xs text-red-700 mt-0.5">{alert.message}</p>
                  {alert.details?.locations && (
                    <p className="text-[10px] text-red-500 mt-0.5">Locations: {alert.details.locations.join(" / ")}</p>
                  )}
                </div>
                <div className="text-[10px] text-red-400 whitespace-nowrap shrink-0">{fmtTime(alert.timestamp)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ Login Location Tracker ═══ */}
      {loginEvents.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="login-locations-panel">
          <div className="px-5 py-3.5 border-b border-[#eee] bg-[#FDFCF9] flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#1B2B1A]" />
            <h3 className="text-sm font-bold text-[#0A0A0A]">Login Locations (24h)</h3>
            <span className="text-[10px] text-[#999]">{loginEvents.length} events</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#faf7f2] border-b border-[#eee]">
                  <th className="text-left px-4 py-2 font-bold text-[#888] uppercase tracking-wider text-[10px]">User</th>
                  <th className="text-left px-4 py-2 font-bold text-[#888] uppercase tracking-wider text-[10px]">Action</th>
                  <th className="text-left px-4 py-2 font-bold text-[#888] uppercase tracking-wider text-[10px]">IP Address</th>
                  <th className="text-left px-4 py-2 font-bold text-[#888] uppercase tracking-wider text-[10px]">Location</th>
                  <th className="text-left px-4 py-2 font-bold text-[#888] uppercase tracking-wider text-[10px]">Time</th>
                </tr>
              </thead>
              <tbody>
                {loginEvents.slice(0, 15).map((le, i) => (
                  <tr key={i} className="border-b border-[#f5f0e8] hover:bg-[#fdfcf9]">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-[#222]">{le.user_name}</span>
                        <span className={`px-1 py-0.5 rounded text-[7px] font-bold border ${ROLE_COLORS[le.role] || ROLE_COLORS.unknown}`}>{le.role}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] font-bold ${le.action?.includes("failed") ? "text-red-600" : "text-emerald-600"}`}>
                        {le.action?.includes("failed") ? "Failed" : "Success"}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-[#888] text-[10px]">{le.ip_address || "-"}</td>
                    <td className="px-4 py-2 text-[#555]">{le.location || "-"}</td>
                    <td className="px-4 py-2 text-[#bbb]">{fmtTime(le.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Confidentiality Audit Panel ═══ */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="confidentiality-audit-panel">
        <div className="px-5 py-3.5 border-b border-[#eee] flex items-center justify-between bg-[#FDFCF9]">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-[#b89d5a]" />
            <h3 className="text-sm font-bold text-[#0A0A0A]">Deal Access Log — Confidentiality Audit</h3>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold border border-amber-200">COMPLIANCE</span>
          </div>
          <div className="flex items-center gap-1.5">
            {["7d", "30d", "all"].map(f => (
              <button key={f} onClick={() => setViewFilter(f)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-colors ${viewFilter === f ? "bg-[#1B2B1A] text-white" : "bg-[#f5f0e8] text-[#888] hover:text-[#555]"}`}
                data-testid={`view-filter-${f}`}>
                {f === "7d" ? "7 Days" : f === "30d" ? "30 Days" : "All Time"}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#faf7f2] border-b border-[#eee]">
                <th className="text-left px-4 py-2.5 font-bold text-[#888] uppercase tracking-wider text-[10px]">Lender</th>
                <th className="text-left px-4 py-2.5 font-bold text-[#888] uppercase tracking-wider text-[10px]">Deal Number</th>
                <th className="text-left px-4 py-2.5 font-bold text-[#888] uppercase tracking-wider text-[10px]">Location</th>
                <th className="text-left px-4 py-2.5 font-bold text-[#888] uppercase tracking-wider text-[10px]">First Accessed</th>
                <th className="text-left px-4 py-2.5 font-bold text-[#888] uppercase tracking-wider text-[10px]">Last Accessed</th>
                <th className="text-center px-4 py-2.5 font-bold text-[#888] uppercase tracking-wider text-[10px]">Views</th>
              </tr>
            </thead>
            <tbody>
              {lenderViews.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-[#999]">No deal access records found</td></tr>
              ) : (
                filterViews(lenderViews, viewFilter).map((v, i) => (
                  <tr key={i} className="border-b border-[#f5f0e8] hover:bg-[#fdfcf9] transition-colors">
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-[#222]">{v.lender_name}</div>
                      {v.lender_company && <div className="text-[10px] text-[#999]">{v.lender_company}</div>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[#b89d5a] font-bold">{v.deal_number || "-"}</td>
                    <td className="px-4 py-2.5 text-[#666]">{v.property_address || "-"}</td>
                    <td className="px-4 py-2.5 text-[#666]">{fmtDate(v.first_viewed_at)}</td>
                    <td className="px-4 py-2.5 text-[#666]">{fmtDate(v.last_viewed_at)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold text-[10px]">{v.view_count}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ Two-Column: Recent Activity + High Risk ═══ */}
      <div className="grid lg:grid-cols-5 gap-4">

        {/* Recent Activity Feed (3/5 width) */}
        <div className="lg:col-span-3 rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="recent-activity-feed">
          <div className="px-5 py-3.5 border-b border-[#eee] bg-[#FDFCF9] flex items-center gap-2">
            <Activity className="h-4 w-4 text-[#1B2B1A]" />
            <h3 className="text-sm font-bold text-[#0A0A0A]">Recent Activity</h3>
            <span className="text-[9px] text-[#999] ml-1">Last 24h</span>
            <div className="ml-auto flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[9px] text-emerald-600 font-bold">LIVE</span>
            </div>
          </div>
          <div className="max-h-[400px] overflow-y-auto divide-y divide-[#f5f0e8]">
            {recentEntries.length === 0 ? (
              <div className="py-8 text-center text-[#999] text-xs">No recent activity</div>
            ) : (
              recentEntries.slice(0, 20).map((e, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center gap-3 hover:bg-[#fdfcf9] transition-colors" data-testid={`activity-entry-${i}`}>
                  <div className="w-7 h-7 rounded-full bg-[#f5f0e8] flex items-center justify-center shrink-0">
                    <ActionIcon action={e.action} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-semibold text-[#222] truncate">{labelAction(e.action)}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${ROLE_COLORS[e.role] || ROLE_COLORS.unknown}`}>{e.role || "?"}</span>
                    </div>
                    <div className="text-[10px] text-[#888] truncate mt-0.5">
                      {e.user_name || "Unknown"} {e.resolved_entity && e.resolved_entity !== "-" ? `on ${e.resolved_entity}` : ""}
                    </div>
                  </div>
                  <div className="text-[10px] text-[#bbb] whitespace-nowrap shrink-0">{fmtTime(e.timestamp)}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* High Risk Actions + Top Actors (2/5 width) */}
        <div className="lg:col-span-2 space-y-4">
          {/* High Risk Actions */}
          <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="high-risk-panel">
            <div className="px-4 py-3 border-b border-[#eee] bg-[#FDFCF9] flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <h3 className="text-sm font-bold text-[#0A0A0A]">High Risk Actions (24h)</h3>
            </div>
            <div className="max-h-[200px] overflow-y-auto divide-y divide-[#f5f0e8]">
              {highRisk.length === 0 ? (
                <div className="py-6 text-center">
                  <CheckCircle className="h-5 w-5 text-emerald-500 mx-auto mb-1" />
                  <div className="text-xs text-emerald-600 font-semibold">All Clear</div>
                  <div className="text-[10px] text-[#999]">No high-risk actions in the last 24 hours</div>
                </div>
              ) : (
                highRisk.slice(0, 10).map((a, i) => (
                  <div key={i} className="px-4 py-2 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-semibold text-[#222]">{labelAction(a.action)}</div>
                      <div className="text-[10px] text-[#888]">{a.user_name} ({a.role})</div>
                    </div>
                    <div className="text-[10px] text-[#bbb]">{fmtTime(a.timestamp)}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Most Active Users */}
          <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="top-actors-panel">
            <div className="px-4 py-3 border-b border-[#eee] bg-[#FDFCF9] flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              <h3 className="text-sm font-bold text-[#0A0A0A]">Most Active Users (7d)</h3>
            </div>
            <div className="divide-y divide-[#f5f0e8]">
              {topActors.slice(0, 6).map((a, i) => (
                <div key={i} className="px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#f5f0e8] flex items-center justify-center text-[9px] font-bold text-[#888]">{i + 1}</div>
                    <div>
                      <div className="text-xs font-semibold text-[#222]">{a.name}</div>
                      <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${ROLE_COLORS[a.role] || ROLE_COLORS.unknown}`}>{a.role}</span>
                    </div>
                  </div>
                  <div className="text-xs font-bold text-[#b89d5a]">{a.action_count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Route Security Coverage ═══ */}
      <RouteSecurityCoverage />

      {/* ═══ Audit Log Explorer ═══ */}
      <div className="rounded-xl border border-[#e8e0d0] overflow-hidden" data-testid="audit-log-explorer">
        <div className="px-5 py-3.5 border-b border-[#eee] bg-[#FDFCF9] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-[#1B2B1A]" />
            <h3 className="text-sm font-bold text-[#0A0A0A]">Audit Log Explorer</h3>
            <span className="text-[10px] text-[#999]">{fmt(logTotal)} total entries</span>
          </div>
          <button onClick={() => window.open(`${API}/admin/security/audit-logs/export?format=csv`, "_blank")}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border border-[#e8e0d0] text-[#666] hover:bg-[#faf7f2]"
            data-testid="export-audit-btn">
            <Download className="h-3 w-3" /> Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-2.5 border-b border-[#eee] bg-[#faf7f2] flex items-center gap-2 flex-wrap">
          <Filter className="h-3.5 w-3.5 text-[#999]" />
          <select value={logAction} onChange={e => setLogAction(e.target.value)}
            className="text-[10px] px-2 py-1 rounded-lg border border-[#e8e0d0] bg-white text-[#444]"
            data-testid="filter-action">
            <option value="">All Actions</option>
            <option value="auth">Auth Events</option>
            <option value="login">Logins</option>
            <option value="deal">Deal Events</option>
            <option value="stage">Stage Changes</option>
            <option value="lender">Lender Events</option>
            <option value="document">Documents</option>
            <option value="approve">Approvals</option>
            <option value="correction">Corrections</option>
          </select>
          <select value={logEntity} onChange={e => setLogEntity(e.target.value)}
            className="text-[10px] px-2 py-1 rounded-lg border border-[#e8e0d0] bg-white text-[#444]"
            data-testid="filter-entity">
            <option value="">All Entity Types</option>
            <option value="user">User</option>
            <option value="deal">Deal</option>
            <option value="document">Document</option>
            <option value="term_sheet">Term Sheet</option>
            <option value="auth">Auth</option>
          </select>
          <select value={logRole} onChange={e => setLogRole(e.target.value)}
            className="text-[10px] px-2 py-1 rounded-lg border border-[#e8e0d0] bg-white text-[#444]"
            data-testid="filter-role">
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="lender">Lender</option>
            <option value="borrower">Borrower</option>
            <option value="investor">Investor</option>
          </select>
          <select value={logLimit} onChange={e => setLogLimit(Number(e.target.value))}
            className="text-[10px] px-2 py-1 rounded-lg border border-[#e8e0d0] bg-white text-[#444]"
            data-testid="filter-limit">
            <option value={25}>25 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
            <option value={200}>200 rows</option>
          </select>
          <button onClick={() => fetchDashboard(true)} className="px-2 py-1 rounded-lg bg-[#1B2B1A] text-white text-[10px] font-bold hover:bg-[#2D3B2C]"
            data-testid="apply-filters-btn">Apply</button>
        </div>

        {/* Log table — dark terminal style */}
        <div className="bg-[#111] overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="text-[#666] text-left border-b border-[#222]">
                <th className="px-3 py-2 text-[9px] uppercase tracking-wider">Timestamp</th>
                <th className="px-3 py-2 text-[9px] uppercase tracking-wider">Action</th>
                <th className="px-3 py-2 text-[9px] uppercase tracking-wider">User</th>
                <th className="px-3 py-2 text-[9px] uppercase tracking-wider">Role</th>
                <th className="px-3 py-2 text-[9px] uppercase tracking-wider">Entity</th>
                <th className="px-3 py-2 text-[9px] uppercase tracking-wider">Reference</th>
                <th className="px-3 py-2 text-[9px] uppercase tracking-wider">IP</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log, i) => (
                <tr key={i} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a] transition-colors" data-testid={`audit-row-${i}`}>
                  <td className="px-3 py-1.5 text-[#666] whitespace-nowrap">{fmtDate(log.timestamp)}</td>
                  <td className="px-3 py-1.5 text-emerald-400 whitespace-nowrap">{labelAction(log.action)}</td>
                  <td className="px-3 py-1.5 text-[#ccc]">{log.user_name || "Unknown"}</td>
                  <td className="px-3 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                      log.role === "admin" ? "text-purple-400 bg-purple-900/30" :
                      log.role === "lender" ? "text-blue-400 bg-blue-900/30" :
                      log.role === "borrower" ? "text-emerald-400 bg-emerald-900/30" :
                      log.role === "investor" ? "text-amber-400 bg-amber-900/30" :
                      "text-gray-400 bg-gray-800"
                    }`}>{log.role || "?"}</span>
                  </td>
                  <td className="px-3 py-1.5 text-[#888]">{log.entity_type || "-"}</td>
                  <td className="px-3 py-1.5 text-amber-400" title={log.entity_id || "-"}>
                    {log.resolved_entity || log.entity_id?.slice(0, 12) || "-"}
                  </td>
                  <td className="px-3 py-1.5 text-[#555] text-[9px]">{log.ip_address || "-"}</td>
                </tr>
              ))}
              {auditLogs.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-[#555]">No matching audit logs</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ─── Helper Components ───

function SummaryCard({ label, value, sub, icon: Icon, color, bg }) {
  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid={`summary-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
      </div>
      <div className={`text-2xl font-bold ${color} tabular-nums`}>{value}</div>
      <div className="text-[10px] text-[#888] mt-0.5">{label}</div>
      <div className="text-[9px] text-[#bbb] mt-0.5">{sub}</div>
    </div>
  );
}

function ActionIcon({ action }) {
  const a = action || "";
  if (a.includes("login") || a.includes("auth")) return <Lock className="h-3.5 w-3.5 text-purple-500" />;
  if (a.includes("deal") || a.includes("stage")) return <Activity className="h-3.5 w-3.5 text-blue-500" />;
  if (a.includes("document")) return <FileText className="h-3.5 w-3.5 text-amber-500" />;
  if (a.includes("lender")) return <Eye className="h-3.5 w-3.5 text-emerald-500" />;
  if (a.includes("approve") || a.includes("reject")) return <CheckCircle className="h-3.5 w-3.5 text-teal-500" />;
  return <Clock className="h-3.5 w-3.5 text-[#999]" />;
}

function filterViews(views, filter) {
  if (filter === "all") return views;
  const now = new Date();
  const days = filter === "7d" ? 7 : 30;
  const cutoff = new Date(now - days * 86400000).toISOString();
  return views.filter(v => (v.last_viewed_at || "") >= cutoff);
}

function RouteSecurityCoverage() {
  const [coverage, setCoverage] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/security/route-coverage`).then(r => setCoverage(r.data)).catch(() => {});
  }, []);

  if (!coverage) return null;
  const total = coverage.total_routes || 0;
  const audited = coverage.audited_routes || 0;
  const pct = total > 0 ? Math.round((audited / total) * 100) : 0;

  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="route-coverage-panel">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-3.5 flex items-center justify-between bg-[#FDFCF9] border-b border-[#eee] hover:bg-[#faf7f2] transition-colors">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-[#1B2B1A]" />
          <h3 className="text-sm font-bold text-[#0A0A0A]">Route Security Coverage</h3>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${pct >= 80 ? "bg-emerald-100 text-emerald-700" : pct >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
            {pct}% ({audited}/{total})
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 text-[#888] transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && coverage.routes && (
        <div className="p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
          {coverage.routes.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] px-2 py-1 rounded-lg bg-[#faf7f2]">
              <span className={`w-2 h-2 rounded-full ${r.audited ? "bg-emerald-500" : "bg-red-400"}`} />
              <span className="text-[#666] font-mono truncate">{r.method} {r.path}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
